import './App.css';
import {useContext, useEffect, useMemo, useState} from "react";
import {createBrowserRouter, Outlet, RouterProvider, useLocation} from "react-router-dom";
import User from "./model/User.ts";
import {IOmniContext, IOmniEnvironment, OmniContext} from "./OmniContext.ts"
import OmniTitleBar from "./components/OmniTitleBar/OmniTitleBar.tsx";
import {
    AppShell,
    Center,
    Container,
    DEFAULT_THEME,
    Loader,
    MantineProvider,
    useMantineColorScheme
} from "@mantine/core";
import {ModalsProvider} from "@mantine/modals";
import {Notifications} from "@mantine/notifications";
import {useDisclosure, useIsFirstRender, useMediaQuery} from "@mantine/hooks";
import AllPageNotices from "./components/AllPageNotices.js";
import SidebarLinks from "./components/SidebarLinks/SidebarLinks.jsx";
import {ContextMenuProvider} from "mantine-contextmenu";
import OmniSpotlight from "@/components/OmniSpotlight.js";
import ErrorBoundary from "@/components/ErrorBoundary.js";
import OmniFetchTS from "@/util/OmniFetchTS.tsx";
import {useCurrentPath} from "@/util/RouterUtils.ts";
import omni_lettermark from "@/res/omni_lettermark.svg"

// Import styles of packages that you've installed.
// All packages except `@mantine/hooks` require styles imports
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/tiptap/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import '@mantine/schedule/styles.css';
import 'mantine-contextmenu/styles.css';
import {routes} from "@/routes.tsx";
import openReleaseNotesModal, {shouldOpenReleaseNotesModalOnLoad} from "@/components/ReleaseNotesModal/modal_utils.tsx";
import {MANTINE_THEME} from "@/util/MantineTheme.ts";

const keylessRoutes = [
    "/projects",
    "/projects/:id/:tab",
    "/timecards/me/:tab",
    "/bravobits/:tab",
    "/vto/:teamId/:tab",
    "/vto/:teamId",
    "/vto",
    "/scorecard/teams/:id",
    "/cpm/stock"
]

function OutletContainer(props: {state: IOmniContext}) {
    let route = useCurrentPath()
    const location = useLocation()
    const outletComponent = useMemo(() => {
        const keylessOutlet = <Outlet/>
        const keyedOutlet = <Outlet key={location.key}/>
        return keylessRoutes.includes(route as string) ? keylessOutlet : keyedOutlet
    }, [route, location])
    let outletEl = props.state.user == null ? <Center><Loader/></Center> :
            <OmniContext.Provider value={props.state}>
                {(props.state.environment && props.state.user) ? outletComponent : <Center><Loader/></Center>}
            </OmniContext.Provider>
    let outletContainer;
    if (route === "/accountability_chart/*") {
        outletContainer = <Container size={"100%"} pt={"lg"} p={0}>{outletEl}</Container>
    } else if (route === "/scorecard/teams/:id" || route === "/audit" || route === "/vto" || route === "/vto/:teamId" || route === "/vto/:teamId/:tab") {
        outletContainer = <Container size={"98%"} pt={"sm"}>{outletEl}</Container>
    } else if (route === "/cpm_order_tracking") {
        outletContainer = <Container size={"100%"}>{outletEl}</Container>
    } else if (route === "/shipping_request") {
        outletContainer = <Container size={"100%"}>{outletEl}</Container>
    } else {
        outletContainer = <Container size={"xl"} mt={"lg"}>{outletEl}</Container>
    }
    return <>
        <AllPageNotices/>
        {outletContainer}
    </>
}

function UserLoader() {
    const context = useContext(OmniContext)
    const {setColorScheme} = useMantineColorScheme()
    const isFirstRender = useIsFirstRender()
    if (isFirstRender) {
        User.getActiveUser().then((user) => {
            if (user && context.setUser) {
                context.setUser(user)
                setColorScheme(user.pref_dark_mode ? "dark" : "light")
            }
        })
    }
    return <></>
}

export function App() {
    // Global User Object
    const [user, setUserInternal] = useState<User | undefined>(undefined)
    const mobile = useMediaQuery("(max-width: 48em)");
    const [opened, { toggle: toggleOpened }] = useDisclosure(!mobile);

    // Global Environment Settings
    const [environment, setEnvironment] = useState<IOmniEnvironment | null>(null)
    function refreshEnvironment() {
        OmniFetchTS<IOmniEnvironment>("/api/v1/environment", {method: "GET"}).then((json) => {
            if (json.success && json.data) {
                setEnvironment(json.data)
                if (json.data.environmentName && json.data.environmentName.length && json.data.environmentColor) {
                    // document.title = `${json.data.siteName} (${json.data.environmentName})`
                    const img = new Image();
                    const canvas = document.createElement("canvas")
                    canvas.width = 32;
                    canvas.height = 32;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        const link = (document.querySelector("link[rel*='icon']") || document.createElement('link')) as HTMLLinkElement;
                        link.type = 'image/x-icon';
                        link.rel = 'shortcut icon';
                        document.getElementsByTagName('head')[0].appendChild(link);
                        // translatedColor is partiuclar to ColoRosetta - this can be whatever you want
                        img.onload = function() {
                            ctx.drawImage(img, 0, 0);
                            link.href = canvas.toDataURL("image/x-icon");
                        }
                        let lettermark = omni_lettermark
                        let themeColor = DEFAULT_THEME.colors[json.data.environmentColor]
                        const replacements: Record<string, string> = {
                            "#e9f7f6": themeColor[1],
                            "#b3e4e2": themeColor[3],
                            "#5bc6c1": themeColor[4],
                            "#4cc1bb": themeColor[5],
                            "#40bec2": themeColor[6],
                            "#12b2e2": themeColor[7],
                            "#00aeef": themeColor[8]
                        }
                        for (let [value, replacement] of Object.entries(replacements)) {
                            lettermark = lettermark.replaceAll(value.replace("#", "%23"), replacement.replace("#", "%23"))
                        }
                        img.src = lettermark;
                    }
                }
            }
        })
    }

    function setUser(user: User) {
        setUserInternal(user)
        if (shouldOpenReleaseNotesModalOnLoad(user)) {
            openReleaseNotesModal()
        }
    }

    // On Page Load
    useEffect(() => {
        // Reload Environment Data (MOTD, etc) every 5 Minutes
        const MILLIS_IN_FIVE_MINS = 300000
        const environmentInterval = setInterval(refreshEnvironment, MILLIS_IN_FIVE_MINS)

        // Ensure Global User is Populated
        if (user == null) {
            User.getActiveUser(true).then(user => {
                if (user) {
                    setUser(user)
                }
            })
        }

        // Ensure Environment Data is Populated
        if (environment == null) {
            refreshEnvironment()
        }

        // On Component Unmount, clear Interval
        return () => {clearInterval(environmentInterval)}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Data to be made available in OmniContext
    const contextData: IOmniContext = {
        setUser,
        user,
        environment,
        refreshEnvironment,
    }

    const location = useLocation()

    return <MantineProvider theme={MANTINE_THEME}>
        <OmniContext.Provider value={contextData}>
            <UserLoader/>
            <ModalsProvider>
                <ContextMenuProvider borderRadius={"md"}>
                    <Notifications/>
                    <div>
                        <OmniSpotlight/>
                        <Notifications/>
                        <AppShell
                            header={{height: 60}}
                            navbar={{
                                width: 300,
                                breakpoint: "sm",
                                collapsed: {desktop: !opened, mobile: !opened}
                            }}
                        >
                            <AppShell.Header>
                                <OmniTitleBar opened={opened} toggleOpened={toggleOpened}/>
                            </AppShell.Header>
                            <AppShell.Navbar>
                                {(contextData.environment && contextData.user) ?
                                    <SidebarLinks toggleSidebar={toggleOpened}/> :
                                    <Center><Loader/></Center>
                                }
                            </AppShell.Navbar>
                            <AppShell.Main>
                                <ErrorBoundary location={location}>
                                    <OutletContainer state={contextData}/>
                                </ErrorBoundary>
                            </AppShell.Main>
                        </AppShell>
                    </div>
                </ContextMenuProvider>
            </ModalsProvider>
        </OmniContext.Provider>
    </MantineProvider>
}

export function AppRouter() {
    const router = createBrowserRouter(routes);

    return <RouterProvider router={router}/>
}